/**
 * EmailService
 * 
 * Service responsible for handling email operations in the MacSafety web application.
 * Primarily used for resending emails for already submitted forms when users need
 * to send additional copies or resend to different recipients.
 * 
 * Key Features:
 * - Email resending functionality for existing form submissions
 * - PDF generation and caching integration
 * - Automatic handling of cached vs non-cached PDFs
 * - Error handling and user feedback
 * - Authentication token management
 * 
 * Note: This service is NOT used for initial form submission emails, which are
 * handled automatically by the form submission API endpoint. This service is
 * specifically for manual email resending scenarios.
 * 
 * Components that utilize this service:
 * - FormViewComponent: Used when viewing existing submitted forms to resend emails
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { EntraService } from './entra.service';
import { ToastService } from './toast.service';
import { PrintPdfService } from './print-pdf.service';
import { ManualEmailRequest, ManualEmailWithPdfRequest, ManualEmailWithExistingPdfRequest, ManualEmailHtmlRequest, EmailSendResult } from '../models/email-request';

@Injectable({
  providedIn: 'root'
})
export class EmailService {
  // Base API URL for email endpoints
  private readonly apiUrl = environment.apiBaseUrl;

  // Signal for tracking email loading state and user feedback messages
  public emailLoading = signal<{ isLoading: boolean; message: string }>({
    isLoading: false,
    message: ''
  });

  // Signal for storing the result of email send operations
  public emailSendResult = signal<EmailSendResult | null>(null);

  constructor(
    private _httpClient: HttpClient,
    private _toastService: ToastService,
    private _entraService: EntraService,
    private _printPdfService: PrintPdfService
  ) {}

  /**
   * Resets the email loading state to its initial values
   * Clears both the loading flag and any status messages
   */
  public resetEmailLoading(): void {
    this.emailLoading.set({ isLoading: false, message: '' });
  }

  /**
   * Main method for resending emails for existing form submissions
   * Automatically determines whether to use cached PDF or generate a new one
   * 
   * @param request - The email request data including recipients and form information
   * @param formEntryId - The unique identifier of the form entry to resend
   * @param htmlContent - HTML content needed for PDF generation if no cached PDF exists
   * @param attachmentData - Attachment information for PDF generation (images and PDFs)
   * @param onSuccess - Optional callback function to execute on successful email send
   * @param onError - Optional callback function to execute on email send failure
   */
  public async resendEmail(
    request: ManualEmailRequest,
    formEntryId: string,
    htmlContent: string,
    attachmentData: { imageBlobNames: string[]; pdfBlobNames: string[] },
    onSuccess?: () => void,
    onError?: () => void
  ): Promise<void> {
    this.emailLoading.set({ isLoading: true, message: 'Preparing to send email...' });
    
    try {
      // Check if PDF is cached
      const cacheStats = this._printPdfService.getCacheStats();
      const hasCachedPdf = await this.checkIfPdfIsCached(formEntryId);
      
      console.log(`Resending email for form ${formEntryId}. PDF cached: ${hasCachedPdf}`);

      if (hasCachedPdf) {
        // Use cached PDF approach for better performance
        await this.sendEmailWithCachedPdf(request, formEntryId, onSuccess, onError);
      } else {
        // Generate PDF and send email when no cached version exists
        const fullRequest: ManualEmailWithPdfRequest = {
          ...request,
          htmlContent,
          imageBlobNames: attachmentData.imageBlobNames,
          pdfBlobNames: attachmentData.pdfBlobNames
        };
        await this.sendEmailAndGeneratePdf(fullRequest, onSuccess, onError);
      }
    } catch (error) {
      console.error('Error in resendEmail:', error);
      this.handleEmailError('An unexpected error occurred while preparing email', onError, error);
    }
  }

  /**
   * Sends an email with PDF generation from HTML content
   * Used when no cached PDF exists and a new one needs to be generated
   * 
   * @param request - Complete email request including HTML content and attachment data
   * @param onSuccess - Optional callback for successful email send
   * @param onError - Optional callback for error handling
   */
  public async sendEmailAndGeneratePdf(
    request: ManualEmailWithPdfRequest,
    onSuccess?: () => void,
    onError?: () => void
  ): Promise<void> {
    this.emailLoading.set({ isLoading: true, message: 'Generating PDF and sending email...' });
    console.log('Sending email with PDF generation:', request);

    this.sendEmailGeneratePdf(request).subscribe({
      next: (result: EmailSendResult) => {
        console.log('Email sent successfully with generated PDF:', result);
        
        this.emailSendResult.set({
          success: true,
          message: result.message || 'Email sent successfully'
        });

        const recipientText = request.recipients.length === 1 
          ? request.recipients[0]
          : `${request.recipients.length} recipients`;
        
        this._toastService.showSuccessMessage(
          `${result.message}`
        );
        
        onSuccess?.();
      },
      error: (error) => {
        console.error('Error sending email with generated PDF:', error);
        this.handleEmailError('Failed to send email with generated PDF', onError, error);
      },
      complete: () => {
        this.emailLoading.set({ isLoading: false, message: '' });
      }
    });
  }

  /**
   * Sends an email with the form content rendered in the email body (no PDF attachment)
   * Uses the send-email-html endpoint which embeds the HTML content directly
   *
   * @param request - Complete email request including HTML content and attachment data
   * @param onSuccess - Optional callback for successful email send
   * @param onError - Optional callback for error handling
   */
  public async sendEmailHtml(
    request: ManualEmailHtmlRequest,
    onSuccess?: () => void,
    onError?: () => void
  ): Promise<void> {
    this.emailLoading.set({ isLoading: true, message: 'Sending email with form content...' });
    console.log('Sending email with form content in body:', request);

    this.sendEmailHtmlRequest(request).subscribe({
      next: (result: EmailSendResult) => {
        console.log('Email sent successfully with form content in body:', result);

        this.emailSendResult.set({
          success: true,
          message: result.message || 'Email sent successfully'
        });

        this._toastService.showSuccessMessage(
          `${result.message}`
        );

        onSuccess?.();
      },
      error: (error) => {
        console.error('Error sending email with form content in body:', error);
        this.handleEmailError('Failed to send email with form content', onError, error);
      },
      complete: () => {
        this.emailLoading.set({ isLoading: false, message: '' });
      }
    });
  }

  /**
   * Sends an email using an existing cached PDF
   * More efficient than regenerating PDFs for recently viewed forms
   * 
   * @param request - Basic email request data
   * @param formEntryId - Form entry ID to retrieve cached PDF
   * @param onSuccess - Optional callback for successful email send
   * @param onError - Optional callback for error handling
   */
  private async sendEmailWithCachedPdf(
    request: ManualEmailRequest,
    formEntryId: string,
    onSuccess?: () => void,
    onError?: () => void
  ): Promise<void> {
    this.emailLoading.set({ isLoading: true, message: 'Retrieving cached PDF and sending email...' });
    
    try {
      // Get cached PDF data from local storage
      const cachedPdfData = await this.getCachedPdfData(formEntryId);
      if (!cachedPdfData) {
        throw new Error('Cached PDF not found');
      }

      console.log('Sending email with cached PDF:', { 
        formEntryId, 
        pdfFileName: cachedPdfData.filename,
        recipients: request.recipients 
      });

      const fullRequest: ManualEmailWithExistingPdfRequest = {
        ...request,
        pdfFileName: cachedPdfData.filename
      };

      this.sendEmailWithExistingPdf(fullRequest, cachedPdfData.blob).subscribe({
        next: (result: EmailSendResult) => {
          console.log('Email sent successfully with cached PDF:', result);
          
          this.emailSendResult.set({
            success: true,
            message: result.message || 'Email sent successfully'
          });

      
          
          this._toastService.showSuccessMessage(
            `${result.message}`
          );
          
          onSuccess?.();
        },
        error: (error) => {
          console.error('Error sending email with cached PDF:', error);
          this.handleEmailError('Failed to send email with cached PDF', onError, error);
        },
        complete: () => {
          this.emailLoading.set({ isLoading: false, message: '' });
        }
      });
    } catch (error) {
      console.error('Error preparing cached PDF email:', error);
      this.handleEmailError('Failed to retrieve cached PDF', onError, error);
      this.emailLoading.set({ isLoading: false, message: '' });
    }
  }

  /**
   * Checks if a PDF is cached for the given form entry ID
   * Uses local storage to check for existing cached PDFs and validates expiration
   * 
   * @param formEntryId - The form entry ID to check for cached PDF
   * @returns Promise resolving to true if cached PDF exists and is valid, false otherwise
   */
  private async checkIfPdfIsCached(formEntryId: string): Promise<boolean> {
    // This uses your PrintPdfService's private method logic
    // We'll simulate the cache check here
    try {
      const CACHE_PREFIX = 'pdf_cache_';
      const cacheKey = CACHE_PREFIX + formEntryId;
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) return false;

      // Check if not expired (basic check)
      const cachedData = JSON.parse(cached);
      const CACHE_DURATION = 2 * 24 * 60 * 60 * 1000; // 2 days
      const isExpired = (Date.now() - cachedData.timestamp) > CACHE_DURATION;
      
      return !isExpired;
    } catch {
      return false;
    }
  }

  /**
   * Retrieves cached PDF data from local storage
   * Converts base64 data back to Blob for API submission
   * 
   * @param formEntryId - The form entry ID to retrieve cached PDF for
   * @returns Promise resolving to cached PDF data (blob and filename) or null if not found
   */
  private async getCachedPdfData(formEntryId: string): Promise<{blob: Blob, filename: string} | null> {
    try {
      const CACHE_PREFIX = 'pdf_cache_';
      const cacheKey = CACHE_PREFIX + formEntryId;
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) return null;

      const cachedPdf = JSON.parse(cached);
      
      // Convert base64 back to blob for API submission
      const byteCharacters = atob(cachedPdf.blob.split(',')[1]);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      return {
        blob,
        filename: cachedPdf.filename
      };
    } catch (error) {
      console.warn('Failed to retrieve cached PDF data:', error);
      return null;
    }
  }

  /**
   * Centralized error handling for email operations
   * Provides consistent error messaging and handles authentication failures
   * 
   * @param message - User-friendly error message to display
   * @param onError - Optional callback function for custom error handling
   * @param error - The original error object for logging and status checking
   */
  private handleEmailError(message: string, onError?: () => void, error?: any): void {
    this.emailSendResult.set({
      success: false,
      message,
      error: error?.message || 'Unknown error'
    });

    // Handle authentication errors by triggering session refresh
    if (error?.status === 401 || error?.status === 403) {
      this._entraService.sessionRefreshRequiredError();
    } else {
      this._toastService.showErrorMessage(message);
    }

    this.emailLoading.set({ isLoading: false, message: '' });
    onError?.();
  }

  /**
   * HTTP request to send email with PDF generation
   * Handles authentication and sends request to the PDF generation endpoint
   * 
   * @param request - Complete email request with HTML content and attachment data
   * @returns Observable that emits the email send result
   */
  public sendEmailGeneratePdf(request: ManualEmailWithPdfRequest): Observable<EmailSendResult> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const url = `${this.apiUrl}/V1/send-email-generate-pdf`;
        const headers = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`
        });

        const requestData = {
          formType: request.formType,
          formId: request.formId,
          recipients: request.recipients,
          htmlContent: request.htmlContent,
          emailTemplateData: request.emailTemplateData,
          imageBlobNames: request.imageBlobNames,
          pdfBlobNames: request.pdfBlobNames
        };

        const formData = new FormData();
        formData.append('requestData', JSON.stringify(requestData));

        return this._httpClient.post<EmailSendResult>(url, formData, { headers });
      })
    );
  }

  /**
   * HTTP request to send email with the form content in the email body
   * Sends the ManualEmailHtmlRequest model to the send-email-html endpoint
   *
   * @param request - Complete email request with HTML content and attachment data
   * @returns Observable that emits the email send result
   */
  public sendEmailHtmlRequest(request: ManualEmailHtmlRequest): Observable<EmailSendResult> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const url = `${this.apiUrl}/V1/send-email-html-body`;
        const headers = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`
        });

        const requestData: ManualEmailHtmlRequest = {
          formType: request.formType,
          recipients: request.recipients,
          htmlContent: request.htmlContent,
          emailTemplateData: request.emailTemplateData,
          imageBlobNames: request.imageBlobNames,
          pdfBlobNames: request.pdfBlobNames
        };

        const formData = new FormData();
        formData.append('requestData', JSON.stringify(requestData));

        return this._httpClient.post<EmailSendResult>(url, formData, { headers });
      })
    );
  }

  /**
   * HTTP request to send email with existing PDF
   * More efficient than PDF generation when cached version is available
   * 
   * @param request - Email request with existing PDF filename
   * @param pdfBlob - The PDF blob data to send with the email
   * @returns Observable that emits the email send result
   */
  private sendEmailWithExistingPdf(
    request: ManualEmailWithExistingPdfRequest, 
    pdfBlob: Blob
  ): Observable<EmailSendResult> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const url = `${this.apiUrl}/V1/send-email-with-existing-pdf`;
        const headers = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`
        });

        const requestData = {
          formType: request.formType,
          formId: request.formId,
          recipients: request.recipients,
          emailTemplateData: request.emailTemplateData,
          pdfFileName: request.pdfFileName
        };

        const formData = new FormData();
        formData.append('requestData', JSON.stringify(requestData));
        formData.append('pdf', pdfBlob, request.pdfFileName);

        return this._httpClient.post<EmailSendResult>(url, formData, { headers });
      })
    );
  }

  /**
   * Clears the stored email send result
   * Useful for resetting the service state between operations
   */
  public clearResults(): void {
    this.emailSendResult.set(null);
  }

  /**
   * Gets the current email loading state
   * @returns Current loading state object with isLoading flag and message
   */
  public getLoadingState() {
    return this.emailLoading();
  }

  /**
   * Gets the current email send result
   * @returns Current email send result or null if no operation has been performed
   */
  public getSendResult() {
    return this.emailSendResult();
  }
}